/**
 * The provider return, `/auth/callback` (ORDR-63).
 *
 * Modelled on paio-community-ui's `screens/AuthCallback.tsx`. Google sends the
 * browser back here with `?code=` and `?state=`. `useOAuthCallback` POSTs them
 * to `${API_BASE_URL}/oauth/google/callback` with credentials, which sets
 * `paio_sid` on gate.pirouz.ai, then calls `refetch` from `useSession`, which
 * re-reads `/auth/session` with that cookie.
 *
 * NAVIGATION ONLY ON SUCCESS, AND IT IS A DOCUMENT LOAD. `OrderingContextProvider`
 * decides its door and reads the session once, at mount, so an in-page swap to
 * the staff screens would keep the anonymous answer it started with. Replacing
 * the document mounts it fresh, signed in, and `replace` keeps the spent code
 * out of the Back button. There is no router here to do otherwise.
 *
 * ON FAILURE nothing navigates. The screen shows the package's failure state
 * and offers a way back to sign-in, but no retry: the code is single-use, so
 * retrying the same one always fails again.
 *
 * The saved return path is re-validated here, because sessionStorage is not a
 * trust boundary (see `auth/returnPath.ts`).
 *
 * ORDR-61 ADDS ONE STEP BEFORE THE NAVIGATION: if the invitation door left a
 * token, it is accepted here, because accept needs the session this screen has
 * just obtained. See `acceptPendingInvitation` below for why it runs first and
 * why it never throws.
 */

import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  AuthCallbackScreen,
  OAUTH_PROVIDER_STORAGE_KEY,
  RETURN_URL_STORAGE_KEY,
  useOAuthCallback,
} from "@pirouz-ca/ui-auth";
import { useSession } from "@pirouz-ca/ui-hooks";
import { API_BASE_URL, authConfig, SESSION_ENDPOINT } from "../auth/config.js";
import { clearPendingInvitation, readPendingInvitation } from "../auth/pendingInvitation.js";
import { loginHref, safeReturnPath } from "../auth/returnPath.js";

export interface AuthCallbackProps {
  /** Injected for tests. Defaults to replacing the document. */
  readonly navigate?: (path: string) => void;
}

function replaceDocument(path: string): void {
  window.location.replace(path);
}

function savedReturnPath(): string {
  try {
    return safeReturnPath(window.sessionStorage.getItem(RETURN_URL_STORAGE_KEY));
  } catch {
    // Storage switched off: a sign-in that worked is not failed over it.
    return safeReturnPath(null);
  }
}

function clearSignInState(): void {
  try {
    window.sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
    window.sessionStorage.removeItem(OAUTH_PROVIDER_STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/**
 * Accept a pending invitation, if the invitation door left one (ORDR-61).
 *
 * BEFORE THE DOCUMENT IS REPLACED, and that ordering is the point.
 * `OrderingContextProvider` reads the session and then the ordering context
 * once, at mount. Navigating first would mount the staff screens against a
 * session that has no grant yet, and the cook would arrive signed in and
 * unauthorised, which looks exactly like a broken invitation.
 *
 * WITH CREDENTIALS, because paio-auth matches the signed-in email against the
 * invited one. That is also why this cannot happen on the invitation screen,
 * where there is no session to match.
 *
 * NEVER THROWS, AND ALWAYS CLEARS. A refusal here is a real outcome: a
 * wrong-email sign-in is a 403, and the invitation stays unaccepted, by design.
 * The session is genuine either way, so stranding the browser on this screen
 * would be worse than landing without the grant. Clearing regardless keeps a
 * spent or refused token from being replayed on the next sign-in.
 */
async function acceptPendingInvitation(): Promise<void> {
  const token = readPendingInvitation();
  if (token === null) {
    return;
  }
  try {
    await fetch(`${API_BASE_URL}/auth/invitations/${token}/accept`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Offline or refused. The grant is not lost, only not applied yet.
  } finally {
    clearPendingInvitation();
  }
}

export function AuthCallback({ navigate = replaceDocument }: AuthCallbackProps): ReactElement {
  const [params] = useState(() => new URLSearchParams(window.location.search));
  const [returnPath] = useState(savedReturnPath);
  const config = useMemo(() => authConfig(returnPath), [returnPath]);
  // No focus refetch: this screen exists for one round trip.
  const { refetch } = useSession({ endpoint: SESSION_ENDPOINT, refetchOnFocus: false });

  const { status, errorCode } = useOAuthCallback(config, {
    code: params.get("code"),
    state: params.get("state"),
    error: params.get("error"),
    refetch,
  });

  // Once per mount, even if StrictMode runs the effect twice.
  const navigated = useRef(false);
  useEffect(() => {
    if (status !== "succeeded" || navigated.current) {
      return;
    }
    navigated.current = true;
    void (async () => {
      await acceptPendingInvitation();
      clearSignInState();
      navigate(returnPath);
    })();
  }, [status, navigate, returnPath]);

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <AuthCallbackScreen
        status={status}
        errorCode={errorCode}
        copy={{ cancelLabel: "Back to sign-in" }}
        onCancel={() => navigate(loginHref(returnPath))}
      />
    </main>
  );
}
